import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { HighlightsSvg } from '../../assets/svg';
import { EndedSvg, LiveSvg, RunningSvg } from '../../assets/svg/history';
import { CARD_BADGE_WIDTH, CARD_BADGE_HEIGHT } from '../../constants/ui/cardBadge';
import Formatter from '../../i18n/formatter';
import { __ } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import { LightlyWhiteHeaderBackground } from '../../styled-components/shared';
import { SportIds } from '../../types/sports';
import { isOneWeekFromNow } from '../../utils/date';
import TimerContainer from '../DefaultTimer/DefaultTimerContainer';
import FavoriteButton from '../FavoriteButton';
import MatchCardHeaderBadge from '../MatchCardHeaderBadge';
import { name as FullMatchTitleClassName } from '../MatchFullTitle';
import TvIcon from '../TvIcon';

const HEADER_HEIGHT = 2;
const CARD_BADGE_LEFT_SHIFT = 0.45;

const Wrapper = styled.div`
  position: relative;
  will-change: transform;

  width: 100%;
  height: ${HEADER_HEIGHT}em;
  border-top-left-radius: ${theme.Card.borderRadius};
  border-top-right-radius: ${theme.Card.borderRadius};
  padding: 0 1.2em;

  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--MatchCardHeader-mainBackgroundColor, ${theme.MatchCardHeader.backgroundColor});

  user-select: none;
  -webkit-tap-highlight-color: transparent;

  ~ .${FullMatchTitleClassName} {
    padding-top: ${CARD_BADGE_HEIGHT - HEADER_HEIGHT + 0.1}em;
  }
`;

const StyledMatchCardHeaderBadge = styled(MatchCardHeaderBadge)`
  transform: ${({ hasAttach }) => (hasAttach ? `translateX(-${CARD_BADGE_LEFT_SHIFT}em)` : undefined)};
  position: absolute;
  top: 0;
  left: 0;
`;

type StyledFavoriteButtonType = {
  $isSticked: boolean;
  $sportId: number;
  $hasFlag: boolean;
};

const StyledFavoriteButton = styled(FavoriteButton)<StyledFavoriteButtonType>`
  &&& {
    width: 1.8em;
    height: 100%;
    padding: 1px 0 1px 4px;
    margin-left: ${({ $isSticked, $sportId, $hasFlag }) =>
      $isSticked
        ? `${
            $sportId === SportIds.SPECIAL_ODDS || $hasFlag
              ? CARD_BADGE_WIDTH * 1.75 - CARD_BADGE_LEFT_SHIFT - ($hasFlag ? 1.6 : 0.5)
              : CARD_BADGE_WIDTH - 0.8
          }em`
        : `${
            ($sportId === SportIds.SPECIAL_ODDS || $hasFlag
              ? CARD_BADGE_WIDTH * 1.75 - CARD_BADGE_LEFT_SHIFT - ($hasFlag ? 1.6 : 0.5)
              : CARD_BADGE_WIDTH - 0.8) - CARD_BADGE_LEFT_SHIFT
          }em`};
  }
`;

const StyledTvIcon = styled(TvIcon)`
  margin-left: 0.6em;
`;

const StyledHighlightIcon = styled(HighlightsSvg)`
  margin-left: 0.6em;
`;

const StyledEndedIcon = styled(EndedSvg)`
  margin-left: 0.6em;
  width: 0.8em;
  height: 100%;
`;
const StyledLiveIcon = styled(LiveSvg)`
  margin-left: 0.6em;
  width: 0.8em;
  height: 100%;
`;
const StyledPrematchIcon = styled(RunningSvg)`
  margin-left: 0.6em;
  width: 0.8em;
  height: 100%;
`;

const LeftColumn = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  align-items: center;
`;

const CenterColumn = styled.div`
  height: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 50%;
`;

export const StoppageTimeAnnouncedClassName = 'stoppage-time-announced';
const StoppageTimeAnnounced = styled.div`
  display: inline;

  padding: 0.1em 0.5em;
  background-color: #0a0b0f;
  border-radius: 0.3em;
  text-align: center;
  white-space: nowrap;
`;

const LivePeriodLabel = styled.div`
  display: inline;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const MatchCardHeaderTimerClassName = 'card-header-timer';
const Timer = styled.div`
  color: ${theme.MatchCardHeader.Timer.color};
  letter-spacing: 0.18em;
  font-weight: 400;
  font-size: 95%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 100%;

  &:first-letter {
    text-transform: uppercase;
  }

  > *:not(:last-child) {
    margin-right: 0.33em;
  }
`;

const RightColumn = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const RunningWrapper = styled.div``;
const StyledCalendarDate = styled.span`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;

  &:first-letter {
    text-transform: uppercase;
  }
`;

const StyledCount = styled.span`
  display: inline-block;
  color: white;
  margin-left: 0.3em;
`;

type MatchCardHeaderProps = {
  startDate: number | null;
  tvChannel: string | null;
  showFavorite: boolean;
  isFavorite: boolean;
  onFavoriteButtonClicked?: (isFavorite: boolean) => void;
  sportId: number;
  categoryId: number;
  matchId: number;
  isLive?: boolean;
  isOutright?: boolean;
  cardBadgeHasAttach?: boolean;
  periodName?: string;
  className?: string;
  streamHighlightsAvailable?: boolean;
  stoppageTimeAnnouncedNbMins?: number;
  shouldHideTimerInfos?: boolean;
  isMultimatch?: boolean;
  nbEnded?: number;
  nbLive?: number;
  flagId?: string;
  nbPrematch?: number;
  outrightContent?: string;
};

const MatchCardHeader = ({
  startDate,
  tvChannel,
  showFavorite = false,
  sportId,
  categoryId,
  isLive = false,
  isOutright = false,
  cardBadgeHasAttach = false,
  onFavoriteButtonClicked = () => {},
  isFavorite,
  periodName,
  matchId,
  className,
  streamHighlightsAvailable,
  stoppageTimeAnnouncedNbMins,
  isMultimatch,
  nbEnded,
  nbLive,
  nbPrematch,
  flagId,
  shouldHideTimerInfos = false,
  outrightContent = '',
}: MatchCardHeaderProps) => {
  const CenterContent = useMemo(() => {
    if (isLive) {
      if (isOutright) {
        return <RunningWrapper>{outrightContent}</RunningWrapper>;
      }

      return (
        <>
          {periodName ? <LivePeriodLabel>{periodName}</LivePeriodLabel> : null}
          {!shouldHideTimerInfos ? <TimerContainer matchId={matchId} /> : null}
          {typeof stoppageTimeAnnouncedNbMins === 'number' && !shouldHideTimerInfos ? (
            <StoppageTimeAnnounced className={StoppageTimeAnnouncedClassName}>
              {__('+%1$s min', stoppageTimeAnnouncedNbMins)}
            </StoppageTimeAnnounced>
          ) : null}
        </>
      );
    }

    if (isOneWeekFromNow(startDate)) {
      return <StyledCalendarDate>{startDate ? Formatter.formatDate(startDate, 'dateLarge') : ''}</StyledCalendarDate>;
    }

    return <StyledCalendarDate>{startDate ? Formatter.formatCalendar(startDate) : ''}</StyledCalendarDate>;
  }, [
    isLive,
    startDate,
    isOutright,
    periodName,
    matchId,
    stoppageTimeAnnouncedNbMins,
    shouldHideTimerInfos,
    outrightContent,
  ]);

  const renderMultimatchCounters = useCallback(() => {
    if (!isMultimatch) {
      return null;
    }

    return (
      <>
        {nbEnded ? (
          <>
            <StyledEndedIcon />
            <StyledCount>{nbEnded}</StyledCount>
          </>
        ) : null}
        {nbLive ? (
          <>
            <StyledLiveIcon /> <StyledCount>{nbLive}</StyledCount>
          </>
        ) : null}
        {nbPrematch ? (
          <>
            <StyledPrematchIcon /> <StyledCount>{nbPrematch}</StyledCount>
          </>
        ) : null}
      </>
    );
  }, [isMultimatch, nbEnded, nbLive, nbPrematch]);

  return (
    <Wrapper className={className}>
      <LightlyWhiteHeaderBackground />
      <LeftColumn>
        <StyledMatchCardHeaderBadge
          isLive={isLive}
          sportId={sportId}
          categoryId={categoryId}
          flagId={flagId}
          isMultimatch={isMultimatch}
          hasAttach={cardBadgeHasAttach}
        />
        {showFavorite ? (
          <StyledFavoriteButton
            isFavorite={isFavorite}
            onClick={() => onFavoriteButtonClicked(isFavorite)}
            $isSticked={!cardBadgeHasAttach}
            $sportId={sportId}
            $hasFlag={!!flagId}
          />
        ) : null}
      </LeftColumn>
      <CenterColumn>
        <Timer className={MatchCardHeaderTimerClassName}>{CenterContent}</Timer>
      </CenterColumn>
      <RightColumn>
        {isMultimatch ? renderMultimatchCounters() : null}
        {streamHighlightsAvailable ? <StyledHighlightIcon /> : null}
        {tvChannel ? <StyledTvIcon tvChannel={tvChannel} /> : null}
      </RightColumn>
    </Wrapper>
  );
};

export default React.memo<MatchCardHeaderProps>(MatchCardHeader);
